import type { AccessorialRateListQuery } from '@chukua/common';
import { Button, Popconfirm, Tag, type TableColumnsType } from 'antd';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { formatMoney } from '../../../../../shared/policies/format-money';
import { ACCESSORIAL_UNIT_LABELS } from '../../../policies/rate-card-labels';
import type { AccessorialRateView, AccessorialType } from '../../../rate-cards.api';
import { canChangePrice, rateLineStatus } from '../../../policies/rate-line-status';

interface BuildAccessorialColumnsArgs {
  list: Pick<ListQueryControls<AccessorialRateListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  types: AccessorialType[];
  today: string;
  canEdit: boolean;
  onChange: (rate: AccessorialRateView) => void;
  onEnd: (rate: AccessorialRateView) => void;
}

const STATUS_TAGS = {
  current: { label: 'Current', color: 'success' },
  upcoming: { label: 'Starts later', color: 'processing' },
  superseded: { label: 'Ended', color: 'default' },
  ended: { label: 'Ended', color: 'default' },
} as const;

/** Each column's `key` is its `sort` field or filter param in `accessorialRateListQuerySchema`. */
export function buildAccessorialColumns({ list, types, today, canEdit, onChange, onEnd }: BuildAccessorialColumnsArgs): TableColumnsType<AccessorialRateView> {
  const typeOf = (code: string) => types.find((type) => type.code === code);
  const windowOf = (rate: AccessorialRateView) => ({ ...rate, supersededByLineId: null });

  return [
    {
      title: 'Charge',
      key: 'accessorialTypeCode',
      filters: types.map((type) => ({ text: type.name, value: type.code })),
      filteredValue: list.filteredValueFor('accessorialTypeCode'),
      render: (_, rate) => typeOf(rate.accessorialTypeCode)?.name ?? rate.accessorialTypeCode,
    },
    {
      title: 'Price',
      key: 'amountMinor',
      sorter: true,
      sortOrder: list.sortOrderFor('amountMinor'),
      render: (_, rate) => {
        const type = typeOf(rate.accessorialTypeCode);
        return `${formatMoney(rate.amount)}${type ? ` · ${ACCESSORIAL_UNIT_LABELS[type.unit].toLowerCase()}` : ''}`;
      },
    },
    { title: 'Free quantity', dataIndex: 'freeQuantity', key: 'freeQuantity', render: (quantity: number | null) => quantity ?? '—' },
    { title: 'From', dataIndex: 'validFrom', key: 'validFrom', sorter: true, sortOrder: list.sortOrderFor('validFrom') },
    { title: 'To', dataIndex: 'validTo', key: 'validTo', render: (validTo: string | null) => validTo ?? 'Open-ended' },
    {
      title: 'Status',
      key: 'status',
      render: (_, rate) => {
        const tag = STATUS_TAGS[rateLineStatus(windowOf(rate), today)];
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_, rate) =>
        canEdit &&
        canChangePrice(windowOf(rate), today) && (
          <div className="flex gap-1">
            <Button size="small" type="link" onClick={() => onChange(rate)}>
              Change price
            </Button>
            <Popconfirm
              title="End this rate today?"
              description="It stops applying after today. Its history stays."
              okText="End rate"
              onConfirm={() => onEnd(rate)}
            >
              <Button size="small" type="link" danger>
                End
              </Button>
            </Popconfirm>
          </div>
        ),
    },
  ];
}
